/**
 * Overlay actions rapides pour les cartes SmartFeed découverte.
 * (cache_key commence par "discovery-event-" ou button_target par "/discovery/events/")
 *
 * Rangée 1 : « 👍 Intéressé » / « 👎 Pas intéressé » (NOT_INTERESTED masque la carte).
 * Rangée 2 : « 🗓 Ajouter au calendrier » (toggle add/remove, agenda Life réel)
 *            + « ⚙️ » pour ajuster ses préférences de découverte depuis le feed.
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, IconButton, Tooltip } from '@mui/material'
import {
  addEventToCalendar,
  recordEventInteraction,
  removeEventFromCalendar,
} from '../../utils/discoveryApi'

const btnSx = (color) => ({
  flex: 1,
  background: `rgba(${color}, 0.2)`,
  border: `1px solid rgba(${color}, 0.5)`,
  color: `rgb(${color})`,
  fontSize: '12px',
  padding: '8px 12px',
  borderRadius: '6px',
  '&:hover': {
    background: `rgba(${color}, 0.3)`,
    border: `1px solid rgba(${color}, 0.8)`,
  },
})

const DiscoveryCardActions = ({ publication, onRemove }) => {
  const navigate = useNavigate()
  const [inCalendar, setInCalendar] = useState(false)
  const [calendarBusy, setCalendarBusy] = useState(false)

  // Détecte si c'est une carte découverte
  const isDiscoveryCard = () => {
    const cacheKey = publication.cache_key || ''
    const buttonTarget = publication.button_target || ''
    return cacheKey.startsWith('discovery-event-') || buttonTarget.startsWith('/discovery/events/')
  }

  // Extrait l'event ID du button_target (/discovery/events/123)
  const getEventId = () => {
    const match = (publication.button_target || '').match(/\/discovery\/events\/(\d+)/)
    return match ? parseInt(match[1], 10) : null
  }

  const handleInterested = async (e) => {
    e.stopPropagation()
    const eventId = getEventId()
    if (eventId) {
      try {
        await recordEventInteraction(eventId, 'INTERESTED', { origin: 'smartfeed' })
      } catch (err) {
        console.error('Failed to record interaction:', err)
      }
    }
  }

  const handleNotInterested = async (e) => {
    e.stopPropagation()
    const eventId = getEventId()
    if (eventId) {
      try {
        await recordEventInteraction(eventId, 'NOT_INTERESTED', { origin: 'smartfeed' })
        // Masque la carte localement avec un délai
        setTimeout(() => onRemove?.(publication.id), 300)
      } catch (err) {
        console.error('Failed to record interaction:', err)
      }
    }
  }

  const handleCalendarToggle = async (e) => {
    e.stopPropagation()
    const eventId = getEventId()
    if (!eventId || calendarBusy) return
    setCalendarBusy(true)
    try {
      if (inCalendar) {
        await removeEventFromCalendar(eventId)
        setInCalendar(false)
      } else {
        await addEventToCalendar(eventId, { origin: 'smartfeed' })
        setInCalendar(true)
      }
    } catch (err) {
      console.error('Calendar toggle failed:', err)
    } finally {
      setCalendarBusy(false)
    }
  }

  const handlePreferences = (e) => {
    e.stopPropagation()
    navigate('/discovery/preferences')
  }

  if (!isDiscoveryCard()) {
    return null
  }

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <IconButton size="small" onClick={handleInterested} sx={btnSx('74, 222, 128')}>
          👍 Intéressé
        </IconButton>

        <IconButton size="small" onClick={handleNotInterested} sx={btnSx('248, 113, 113')}>
          👎 Pas intéressé
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <IconButton
          size="small"
          onClick={handleCalendarToggle}
          disabled={calendarBusy}
          sx={btnSx(inCalendar ? '167, 139, 250' : '96, 165, 250')}
        >
          {inCalendar ? '✓ Dans ton agenda' : '🗓 Ajouter au calendrier'}
        </IconButton>

        <Tooltip title="Ajuster mes préférences de découverte">
          <IconButton
            size="small"
            onClick={handlePreferences}
            sx={{ ...btnSx('148, 163, 184'), flex: '0 0 auto' }}
          >
            ⚙️
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default DiscoveryCardActions
